import React, { useEffect, useRef, useState } from 'react'
import PreviewWall from '../components/PreviewWall'
import {
  CODES, MEETING_PREVIEW, SOON_MS, FLASH_MS,
  fmtPrice, fmtTime, fmtCountdown, splitName,
  RunnerMark, BookLogo, BookLink, Segmented, BookFilter, NextToGo, RacingSlip, FlucMove, MoverMark,
  buildRunners, racingLeg, useRacingBoard, previewBlur,
} from './racingShared'
import FlucsModal from './FlucsModal'
import './RacingOddsMobile.css'

// Mobile layout. Same board as desktop (racingShared.jsx), drawn as one card
// per runner: best price first, tap a card for every bookmaker best to worst.

export default function RacingOddsMobile({ isVisible, isPreview: previewProp }) {
  const {
    isPreview, hadTrial, status, loading, slip,
    code, changeCode, market, setMarket,
    selectedId, setSelectedId, selected, now,
    hiddenBooks, changeHiddenBooks, bookOrder, changeBookOrder, orderBooks, allBooks,
    openMeetings, toggleMeeting, mobileView, changeMobileView,
    flashes, flucs, upcoming, ahead, meetings,
  } = useRacingBoard({ isVisible, isPreview: previewProp, withFlucs: true })
  const [sheetOpen, setSheetOpen] = useState(false)

  const pick = (id) => {
    setSelectedId(id)
    setSheetOpen(false)
  }

  return (
    <div className="rc-page rcm-page">
      <div className="rcm-codes">
        <Segmented label="Racing code" options={CODES} value={code} onChange={changeCode} icons />
      </div>
      <NextToGo races={ahead} now={now} selectedId={selectedId} onSelect={setSelectedId} />

      {status === 'reconnecting' && <div className="rc-notice">Reconnecting to the odds feed…</div>}

      {status === 'too_many_tabs' ? (
        <div className="rc-empty">Racing Odds is already open in 2 other tabs or devices. Close one, then reload this page.</div>
      ) : status === 'no_access' ? (
        <div className="rc-empty">Your plan doesn't include Racing Odds. It comes with Promo Premium and Full Access.</div>
      ) : selected ? (
        <MobileBoard
          race={selected}
          code={code}
          market={market}
          onMarket={setMarket}
          now={now}
          books={orderBooks(Object.keys(selected.books).filter((b) => !hiddenBooks.has(b)))}
          flashes={flashes}
          flucs={flucs}
          siblings={upcoming.filter((r) => r.track === selected.track && r.country === selected.country)}
          nextAhead={ahead}
          onSelect={setSelectedId}
          onOpenMeetings={() => setSheetOpen(true)}
          view={mobileView}
          onView={changeMobileView}
          slip={slip}
          filter={
            <BookFilter
              books={allBooks}
              hidden={hiddenBooks}
              onChange={changeHiddenBooks}
              customOrder={bookOrder.length > 0}
              onResetOrder={() => changeBookOrder([])}
              onReorder={changeBookOrder}
            />
          }
          isPreview={isPreview}
        />
      ) : loading || ahead.length > 0 ? (
        <MobileSkeleton />
      ) : (
        <div className="rc-empty">No upcoming races right now. New races appear about 24 hours before they start.</div>
      )}

      {isPreview && (
        <div className="rcm-wall">
          <PreviewWall
            title="Every bookmaker's racing prices, live, side by side"
            features={['18 bookmakers compared on every runner', 'Thoroughbred, harness and greyhound', 'Prices update the moment a bookmaker moves', 'Best price highlighted for every runner']}
            planName="Promo Premium"
            accent="#a78bfa"
            hadTrial={hadTrial}
          />
        </div>
      )}

      <RacingSlip slip={slip} compact />

      {sheetOpen && (
        <MeetingsSheet
          meetings={meetings}
          now={now}
          selectedId={selectedId}
          openMeetings={openMeetings}
          onToggleMeeting={toggleMeeting}
          onPick={pick}
          onClose={() => setSheetOpen(false)}
        />
      )}
    </div>
  )
}

// ---------- the race ----------

function MobileBoard({ race, code, market, onMarket, now, books, flashes, flucs, siblings, nextAhead, onSelect, onOpenMeetings, view, onView, slip, filter, isPreview }) {
  const [openRunner, setOpenRunner] = useState(null)
  // Tapping a card's flucs line opens that runner's chart sheet, by number so it follows live updates.
  const [chartFor, setChartFor] = useState(null)
  useEffect(() => {
    setOpenRunner(null)
    setChartFor(null)
  }, [race.race_id])
  const { active, scratched } = buildRunners(race, books, market, false, flucs)
  const chartRunner = chartFor == null ? null : active.find((r) => r.number === chartFor)

  const sorted = [...siblings].sort((a, b) => a.race_number - b.race_number)
  const idx = sorted.findIndex((r) => r.race_id === race.race_id)
  const prev = sorted[idx - 1]
  const next = sorted[idx + 1]
  // Jumped = the API says the race has closed (2+ books shut betting). Past its
  // start but not closed = overdue: prices stay live, countdown reads "+1:24".
  const jumped = !!race.closed
  const left = race.start - now
  const followOn = jumped
    ? (sorted.find((r) => r.race_number > race.race_number && !r.closed && r.race_id !== race.race_id) ||
       nextAhead.find((r) => r.race_id !== race.race_id))
    : null
  const flashFor = (book, number) => {
    const f = flashes.get(`${race.race_id}|${book}|${number}|${market}`)
    return f && now - f.at < FLASH_MS ? f : null
  }

  return (
    <section className="rcm-board">
      <div className="rcm-race-head">
        <button className="rcm-step" disabled={!prev} onClick={() => prev && onSelect(prev.race_id)} aria-label="Previous race">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
        </button>
        <button className="rcm-race-pick" onClick={onOpenMeetings} aria-label="Choose a race">
          <span className="rcm-race-title">{race.track} <span className="rcm-race-num">R{race.race_number}</span></span>
          <span className="rcm-race-meta">
            {fmtTime(race.start)}
            {race.abandoned ? (
              <span className="rc-chip rc-chip-red">Abandoned</span>
            ) : (
              <span className={`rc-chip${jumped ? ' rc-chip-muted' : left < SOON_MS ? ' rcm-chip-soon' : ''}`}>{jumped ? 'Jumped' : fmtCountdown(left)}</span>
            )}
          </span>
          <svg className="rcm-race-chev" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
        </button>
        <button className="rcm-step" disabled={!next} onClick={() => next && onSelect(next.race_id)} aria-label="Next race">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
        </button>
      </div>

      <div className="rcm-tools">
        <Segmented label="Market" options={[{ id: 'win', label: 'Win' }, { id: 'place', label: 'Place' }]} value={market} onChange={onMarket} />
        <ViewToggle value={view} onChange={onView} />
        {filter}
      </div>

      {followOn && (
        <button className="rcm-follow" onClick={() => onSelect(followOn.race_id)}>
          Next: {followOn.track} R{followOn.race_number}
        </button>
      )}

      {view === 'grid' ? (
        <MobileGrid race={race} code={code} market={market} now={now} books={books} active={active} flashFor={flashFor} jumped={jumped} slip={slip} isPreview={isPreview} />
      ) : (
      <div className={`rcm-runners${jumped ? ' is-jumped' : ''}`}>
        {active.map((rn, rowIdx) => {
          const open = openRunner === rn.number
          const blur = isPreview && rowIdx >= 2
          const bestFlash = rn.bestBooks.map((b) => flashFor(b, rn.number)).find(Boolean)
          // Every shown book's price for this runner, best first, no-price last.
          const ladder = books
            .map((b, i) => ({ book: b, price: rn.prices[i], url: race.books[b].url }))
            .sort((a, b) => (b.price ?? -1) - (a.price ?? -1))
          return (
            <div key={rn.number} className={`rcm-runner${open ? ' is-open' : ''}${blur ? ' is-blurred' : ''}`}>
              <button
                className="rcm-runner-row"
                onClick={() => !isPreview && setOpenRunner(open ? null : rn.number)}
                aria-expanded={open}
              >
                <RunnerMark key={`${race.race_id}-${rn.number}`} number={rn.number} silk={rn.silk} code={code} />
                <span className="rcm-runner-main">
                  <span className="rcm-runner-name">
                    <span className="rcm-runner-text">{rn.name}</span>
                    <MoverMark mover={rn.mover} />
                    {rn.emergency && <span className="rc-em">EM</span>}
                  </span>
                  {rn.flucLine?.length > 1 && (
                    // inside the card's button, so a span: tapping it opens the chart, not the ladder
                    <span
                      className="rcm-fluc"
                      role="button"
                      tabIndex={0}
                      aria-label={`Price chart for ${rn.name}`}
                      onClick={(e) => {
                        e.stopPropagation()
                        if (!isPreview) setChartFor(rn.number)
                      }}
                      onKeyDown={(e) => {
                        if (e.key !== 'Enter' && e.key !== ' ') return
                        e.preventDefault()
                        e.stopPropagation()
                        if (!isPreview) setChartFor(rn.number)
                      }}
                    >
                      <FlucMove moves={rn.flucLine} />
                      <svg className="rcm-fluc-ico" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 3v18h18" /><polyline points="7 14 11 10 14 13 20 7" /></svg>
                    </span>
                  )}
                  <span className="rc-chance"><span style={{ width: `${Math.min(100, rn.chance * 100 * 2.2)}%` }} /></span>
                </span>
                {rn.best != null ? (
                  <span className="rcm-best">
                    {bestFlash && <span key={bestFlash.at} className={`rc-flash rc-flash-${bestFlash.dir}`} />}
                    <span className="rcm-best-price">{fmtPrice(rn.best)}</span>
                    <span className="rc-best-logo">
                      <BookLogo book={rn.bestBooks[0]} size={26} />
                      {rn.bestBooks.length > 1 && <span className="rc-best-more">+{rn.bestBooks.length - 1}</span>}
                    </span>
                  </span>
                ) : <span className="rc-dash">–</span>}
              </button>

              {open && (
                <div className="rcm-ladder">
                  {ladder.map(({ book, price, url }) => {
                    const f = flashFor(book, rn.number)
                    const bookClosed = race.books[book].status === 'closed'
                    const isBest = price != null && price === rn.best && (!bookClosed || jumped)
                    const leg = price != null && !bookClosed && !jumped ? racingLeg(race, book, rn, market, price) : null
                    const inSlip = leg && slip.has(leg)
                    const cls = `rcm-ladder-item${isBest ? ' is-best' : ''}${inSlip ? ' is-in-slip' : ''}${price == null ? ' is-empty' : ''}${bookClosed ? ' is-closed' : ''}`
                    return (
                      <div key={book} className={cls}>
                        {f && <span key={f.at} className={`rc-flash rc-flash-${f.dir}`} />}
                        {/* logo opens the bookmaker, the price goes in the betslip */}
                        <BookLink book={book} url={url} size={22} />
                        <button className="rcm-ladder-price" disabled={!leg} onClick={() => leg && slip.toggle(leg)}>
                          {price == null ? '–' : fmtPrice(price)}
                        </button>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </div>
      )}

      {chartRunner && (
        <FlucsModal race={race} runner={chartRunner} books={books} code={code} market={market} now={now} onClose={() => setChartFor(null)} compact />
      )}

      {scratched.length > 0 && (
        <details className="rc-scratched rcm-scratched">
          <summary>Scratched ({scratched.length})</summary>
          <ul>
            {scratched.map((r) => (
              <li key={r.number}><span className="rc-scratched-num">{r.number}</span>{splitName(r.rawName).name}</li>
            ))}
          </ul>
        </details>
      )}
    </section>
  )
}

function MobileSkeleton() {
  return (
    <section className="rcm-board" aria-label="Loading race">
      <div className="rcm-race-head rcm-skel-head">
        <span className="rc-skel" style={{ width: 36, height: 36, borderRadius: 10 }} />
        <span className="rcm-skel-title">
          <span className="rc-skel" style={{ width: 150, height: 15 }} />
          <span className="rc-skel" style={{ width: 90, height: 11 }} />
        </span>
        <span className="rc-skel" style={{ width: 36, height: 36, borderRadius: 10 }} />
      </div>
      {Array.from({ length: 7 }, (_, i) => (
        <div key={i} className="rcm-skel-row">
          <span className="rc-skel" style={{ width: 36, height: 36, borderRadius: 10 }} />
          <span className="rcm-skel-main">
            <span className="rc-skel" style={{ width: `${70 - (i % 3) * 12}%`, height: 12 }} />
            <span className="rc-skel" style={{ width: '45%', height: 3 }} />
          </span>
          <span className="rc-skel" style={{ width: 76, height: 22, borderRadius: 7 }} />
        </div>
      ))}
    </section>
  )
}

// Cards (one runner at a time, best first) or Grid (every book side by side).
function ViewToggle({ value, onChange }) {
  return (
    <div className="rc-seg rcm-view" role="tablist" aria-label="Layout">
      <button role="tab" aria-selected={value === 'cards'} aria-label="Cards" title="Cards" className="rc-seg-btn" onClick={() => onChange('cards')}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="6" rx="2" /><rect x="3" y="14" width="18" height="6" rx="2" /></svg>
      </button>
      <button role="tab" aria-selected={value === 'grid'} aria-label="Grid" title="Grid" className="rc-seg-btn" onClick={() => onChange('grid')}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M3 15h18M9 3v18M15 3v18" /></svg>
      </button>
    </div>
  )
}

// Every shown book side by side: runner column pinned, swipe for more books.
// Column order and hidden books come from the same saved settings as desktop.
function MobileGrid({ race, code, market, now, books, active, flashFor, jumped, slip, isPreview }) {
  return (
    <div className={`rcm-grid-wrap${jumped ? ' is-jumped' : ''}`}>
      <table className="rcm-grid">
        <thead>
          <tr>
            <th className="rcm-g-runner" />
            {books.map((b) => (
              <th key={b} className="rcm-g-book" title={b}><BookLink book={b} url={isPreview ? null : race.books[b].url} size={24} /></th>
            ))}
          </tr>
        </thead>
        <tbody>
          {active.map((rn, rowIdx) => (
            <tr key={rn.number}>
              <td className="rcm-g-runner">
                <span className="rcm-g-runner-in">
                  <RunnerMark key={`${race.race_id}-${rn.number}`} number={rn.number} silk={rn.silk} code={code} />
                  <span className="rcm-g-name">{rn.name}</span>
                </span>
              </td>
              {books.map((b, i) => {
                const p = rn.prices[i]
                const f = flashFor(b, rn.number)
                const bookClosed = race.books[b].status === 'closed'
                const leg = p != null && !bookClosed && !jumped ? racingLeg(race, b, rn, market, p) : null
                const inSlip = leg && slip.has(leg)
                const isBest = p != null && p === rn.best && (!bookClosed || jumped)
                const cls = `rcm-g-book rcm-g-price${isBest ? ' is-best' : ''}${inSlip ? ' is-in-slip' : ''}${p == null ? ' is-empty' : ''}${bookClosed ? ' is-closed' : ''}${isPreview && previewBlur(rowIdx, i) ? ' is-blurred' : ''}`
                return (
                  <td key={b} className={cls}>
                    {f && <span key={f.at} className={`rc-flash rc-flash-${f.dir}`} />}
                      {leg && !isPreview
                      ? <button className="rcm-g-btn" onClick={() => slip.toggle(leg)}>{fmtPrice(p)}</button>
                      : <span>{p == null ? '–' : fmtPrice(p)}</span>}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ---------- meetings sheet ----------

function MeetingsSheet({ meetings, now, selectedId, openMeetings, onToggleMeeting, onPick, onClose }) {
  // The parent re-renders every second, so onClose is pinned in a ref and this
  // runs once per opening (otherwise the body lock would re-save itself as 'hidden').
  const closeRef = useRef(onClose)
  closeRef.current = onClose
  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape') closeRef.current() }
    document.addEventListener('keydown', esc)
    // Keep the page behind still while the sheet is up.
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', esc)
      document.body.style.overflow = prevOverflow
    }
  }, [])

  return (
    <div className="rcm-sheet-wrap" onClick={onClose}>
      <div className="rcm-sheet" role="dialog" aria-label="Meetings" onClick={(e) => e.stopPropagation()}>
        <div className="rcm-sheet-grab" />
        <div className="rcm-sheet-head">
          <span>Meetings</span>
          <button className="rcm-sheet-close" onClick={onClose} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>
        <div className="rcm-sheet-body">
          {meetings.length === 0 && <div className="rc-rail-empty">No upcoming races</div>}
          {meetings.map((list) => {
            const key = `${list[0].track}|${list[0].country}`
            const open = openMeetings.has(key)
            const extra = list.length - MEETING_PREVIEW
            const shown = open ? list : list.filter((r, i) => i < MEETING_PREVIEW || r.race_id === selectedId)
            return (
              <div key={key} className="rc-meeting">
                <button className="rc-meeting-head" onClick={() => extra > 0 && onToggleMeeting(key)} disabled={extra <= 0} aria-expanded={extra > 0 ? open : undefined}>
                  <span className="rc-meeting-name">{list[0].track}</span>
                  {list[0].country !== 'AU' && <span className="rc-meeting-country">{list[0].country}</span>}
                  {extra > 0 && (
                    <span className="rc-meeting-more">
                      {open ? 'Less' : `+${extra}`}
                      <svg className={open ? 'is-open' : undefined} width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
                    </span>
                  )}
                </button>
                {shown.map((r) => (
                  <button
                    key={r.race_id}
                    className={['rc-race-row', r.race_id === selectedId && 'is-selected', r.start - now < SOON_MS && 'is-soon', r.abandoned && 'is-abandoned'].filter(Boolean).join(' ')}
                    onClick={() => onPick(r.race_id)}
                  >
                    <span className="rc-race-row-num">R{r.race_number}</span>
                    <span className="rc-race-row-time">{fmtTime(r.start)}</span>
                    <span className="rc-race-row-cd">{r.abandoned ? 'Abandoned' : fmtCountdown(r.start - now)}</span>
                  </button>
                ))}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
